import { useId, useRef, useState } from "react";
import { cn } from "../lib/utils";
import type { AuteurJeu, QuoiJournalJeu, TexteAvantJeu } from "./contract";
import { quandEnClair } from "./dates-en-clair";
import { ALERTE_PANNE, BOUTON_CONTOUR, Carte, CHAMP } from "./livres-jeux-commun";
import { messageDEchec, useTextesAvant } from "./livres-jeux-requetes";
import { LIBELLE_ORIGINE } from "./livres-jeux-textes";

/**
 * Les textes d'avant (#276) : ce que le journal du livre garde d'un texte remplace, celui de la
 * trame ou d'une scene (reecrite, importee par-dessus, supprimee), lisible et copiable. Lus a
 * l'ouverture seulement : un texte peut faire 20 000 caracteres, et il y en a vingt.
 */

/** L'ecriture qui a remplace le texte, en clair. */
function avant(quoi: QuoiJournalJeu, par: AuteurJeu): string {
  const qui = LIBELLE_ORIGINE[par];
  if (quoi === "import") return `Avant l'import de ${qui}`;
  if (quoi === "scene_supprimee") return `Avant la suppression par ${qui}`;
  return `Avant la réécriture de ${qui}`;
}

type Copie = "copie" | "selection" | null;

async function copier(texte: string, zone: HTMLTextAreaElement | null): Promise<Copie> {
  try {
    await navigator.clipboard.writeText(texte);
    return "copie";
  } catch {
    zone?.focus();
    zone?.select();
    return "selection";
  }
}

export function TextesAvant({ livreId, scene }: { livreId: number; scene: string | null }) {
  const [ouvert, setOuvert] = useState(false);
  const lecture = useTextesAvant(livreId, scene, ouvert);
  const id = useId();
  const idTitre = `${id}-titre`;
  const idListe = `${id}-liste`;

  return (
    <Carte id={idTitre} titre="Textes d'avant">
      <p className="text-sm text-muted-foreground">
        {scene === null
          ? "La trame d'avant chaque réécriture, gardée par le journal du livre."
          : "Le texte d'avant chaque réécriture, import ou suppression d'une scène de cet identifiant, gardé par le journal du livre."}{" "}
        Les vingt derniers, à relire ou à copier.
      </p>
      <div>
        <button
          aria-controls={idListe}
          aria-expanded={ouvert}
          className={BOUTON_CONTOUR}
          onClick={() => setOuvert(!ouvert)}
          type="button"
        >
          {ouvert ? "Masquer les textes d'avant" : "Voir les textes d'avant"}
        </button>
      </div>
      <div id={idListe}>
        {!ouvert ? null : lecture.isPending ? (
          <p className="text-sm text-muted-foreground" role="status">
            Lecture du journal...
          </p>
        ) : lecture.isError ? (
          <p className={ALERTE_PANNE} role="alert">
            {messageDEchec(lecture.error)}
          </p>
        ) : lecture.data.textes.length === 0 ? (
          <p className="text-sm text-muted-foreground" role="status">
            Aucun texte d'avant : rien n'a encore été remplacé.
          </p>
        ) : (
          <ol aria-labelledby={idTitre} className="flex flex-col gap-4">
            {lecture.data.textes.map((texte) => (
              <TexteAvant key={texte.id} texte={texte} />
            ))}
          </ol>
        )}
      </div>
    </Carte>
  );
}

function TexteAvant({ texte }: { texte: TexteAvantJeu }) {
  const zone = useRef<HTMLTextAreaElement>(null);
  const [copie, setCopie] = useState<Copie>(null);
  const idZone = useId();
  return (
    <li className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" htmlFor={idZone}>
        {avant(texte.quoi, texte.par)}{" "}
        <span className="font-normal text-muted-foreground">
          ({quandEnClair(texte.le, Date.now())})
        </span>
      </label>
      {/* 16 px au telephone : en dessous, iOS agrandit la page au toucher. */}
      <textarea
        className={cn(CHAMP, "min-h-24 resize-y text-base md:text-sm")}
        id={idZone}
        readOnly
        ref={zone}
        rows={4}
        value={texte.texte}
      />
      <span className="inline-flex flex-wrap items-center gap-2">
        <button
          className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
          onClick={() => {
            void copier(texte.texte, zone.current).then(setCopie);
          }}
          type="button"
        >
          Copier ce texte
        </button>
        <span className="text-xs text-muted-foreground" role="status">
          {copie === "copie"
            ? "Copié."
            : copie === "selection"
              ? "Sélectionné : copie-le avec le menu du téléphone."
              : null}
        </span>
      </span>
    </li>
  );
}
